/**
 * PhotoUploader — product-photo dropzone + upload lifecycle (EPIC-006 Phase 7,
 * Task 7.2).
 *
 * Reuses the chat upload state machine via `usePhotoUpload` (sign → S3 POST →
 * poll `/uploads/{id}/status` to clean, purpose `'product-photo'`). When a
 * draft reaches `ready` it calls `onPhotoReady({ id, previewUrl })` so the
 * parent can append it to the gallery `photos[]`; the in-flight draft is then
 * cleared from the uploader. Errors render inline with an i18n message.
 *
 * A native file input (label-wrapped) is the accessible affordance and is the
 * easiest to drive from tests via `fireEvent.change`. ≥44px target.
 */
import { useEffect, useRef, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@garden/ui";

import {
  registerSellerDashboardI18n,
  SELLER_DASHBOARD_NS,
} from "@/features/seller-dashboard/api";
import {
  usePhotoUpload,
  type PhotoDraft,
  type UsePhotoUploadOptions,
} from "@/features/seller-dashboard/usePhotoUpload";

export interface ReadyPhoto {
  /** The clean s3 key (the gallery id + the PATCH `s3_key_original`). */
  s3KeyClean: string;
  /** Local object URL for the immediate preview. */
  previewUrl: string;
}

export interface PhotoUploaderProps {
  onPhotoReady: (photo: ReadyPhoto) => void;
  /**
   * Reports whether any photo is still mid-upload/mid-scan (not yet `ready`
   * or `error`). SCRUM-168: the parent editor uses this to keep Submit/Save
   * disabled — without it, submitting while a photo is still `scanning`
   * silently creates the product with that photo dropped (the draft never
   * makes it into the parent's `photos[]`, since promotion only happens on
   * `ready`), leaving the upload orphaned with no error shown.
   */
  onPendingChange?: (pending: boolean) => void;
  /** Test seam for the poll interval. */
  uploadOptions?: UsePhotoUploadOptions;
}

export function PhotoUploader({
  onPhotoReady,
  onPendingChange,
  uploadOptions,
}: PhotoUploaderProps): ReactElement {
  registerSellerDashboardI18n();
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const { drafts, add, remove } = usePhotoUpload(uploadOptions);
  // Track which drafts have already been promoted so we promote exactly once.
  const promoted = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const d of drafts) {
      if (
        d.state === "ready" &&
        d.s3KeyClean !== null &&
        !promoted.current.has(d.localId)
      ) {
        promoted.current.add(d.localId);
        onPhotoReady({
          s3KeyClean: d.s3KeyClean,
          previewUrl: d.previewUrl ?? "",
        });
        // The gallery now owns the photo AND its object URL — revoking it
        // here would break the thumbnail the gallery just received.
        remove(d.localId, { keepPreviewUrl: true });
      }
    }
  }, [drafts, onPhotoReady, remove]);

  useEffect(() => {
    onPendingChange?.(
      drafts.some((d) => d.state === "uploading" || d.state === "scanning"),
    );
  }, [drafts, onPendingChange]);

  const handleFiles = (files: FileList | null): void => {
    if (files === null) return;
    for (const file of Array.from(files)) {
      void add(file);
    }
  };

  const inFlight = drafts.filter((d) => d.state !== "ready");

  return (
    <div className="flex flex-col gap-3" data-testid="photo-uploader">
      <label
        className={[
          "flex min-h-[88px] cursor-pointer flex-col items-center justify-center gap-1",
          "rounded-[var(--g-radius)] border-2 border-dashed border-border bg-card p-4 text-center",
          "focus-within:ring-2 focus-within:ring-ring",
        ].join(" ")}
        data-testid="photo-dropzone"
      >
        <Icon name="camera" size={20} className="text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">
          {t("photos.drop")}
        </span>
        <span className="text-xs text-muted-foreground">
          {t("photos.dropHint")}
        </span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="sr-only"
          data-testid="photo-input"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </label>

      {inFlight.length > 0 ? (
        <ul className="flex flex-col gap-2" data-testid="photo-drafts">
          {inFlight.map((d) => (
            <DraftRow key={d.localId} draft={d} onRemove={remove} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function DraftRow({
  draft,
  onRemove,
}: {
  draft: PhotoDraft;
  onRemove: (id: string) => void;
}): ReactElement {
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const statusText =
    draft.state === "uploading"
      ? t("photos.uploading")
      : draft.state === "scanning"
        ? t("photos.scanning")
        : draft.errorCode
          ? t(`photos.${draft.errorCode}`)
          : t("photos.uploadFailed");
  const isError = draft.state === "error";
  return (
    <li
      className="flex items-center gap-3 rounded-[var(--g-radius)] border border-border bg-card px-3 py-2"
      data-testid="photo-draft"
    >
      {draft.previewUrl ? (
        <img
          src={draft.previewUrl}
          alt=""
          className="size-10 rounded-[var(--g-radius)] object-cover"
        />
      ) : (
        <div className="size-10 rounded-[var(--g-radius)] bg-muted" />
      )}
      <span
        className={[
          "flex-1 text-sm",
          isError ? "text-[var(--g-error)]" : "text-muted-foreground",
        ].join(" ")}
        role={isError ? "alert" : "status"}
        aria-live="polite"
      >
        {statusText}
      </span>
      <button
        type="button"
        onClick={() => onRemove(draft.localId)}
        aria-label={t("photos.remove")}
        className="inline-flex size-11 items-center justify-center rounded-[var(--g-radius)] text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Icon name="x" size={16} />
      </button>
    </li>
  );
}

export default PhotoUploader;
